import { useState, useEffect, memo } from "react";
import {
  AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem,
  ListItemText, ListItemIcon, Box, CssBaseline, Divider, TextField,
  InputAdornment, Tooltip, Stack,
  Button
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Add, Search, AccountCircle, Payment, School,
  Dashboard, People, ChevronLeft, ChevronRight,
  MonetizationOnRounded,
  Menu,
  Close,
  SupportAgent
} from "@mui/icons-material";
import { Classroom } from "../models/classroom";
import renderAvatar from "./render-avatar";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useStudentActions } from "../hooks/useStudentActions";



interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  isDrawerOpen: boolean;
  isActive?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ 
  icon, 
  label, 
  onClick, 
  isDrawerOpen, 
  isActive = false 
}) => {
  return (
    <ListItem
      onClick={onClick}
      disablePadding
      sx={{
          cursor: 'pointer',
          borderRadius: "12px",
          transition: "all 0.2s ease-in-out",
          border: "1px solid transparent",
          display: "flex",
          justifyContent: isDrawerOpen ? "flex-start" : "center",
          alignItems: "center",
          backgroundColor: isActive ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
          color: isActive ? 'primary.main' : 'text.primary',
          paddingTop: 0.5,
          
        '&:hover': {
          backgroundColor: isActive 
            ? 'rgba(25, 118, 210, 0.2)' 
            : 'rgba(0, 0, 0, 0.04)',
          transform: 'translateX(4px)',
          '& .MuiListItemIcon-root': {
            color: isActive ? 'primary.main' : 'primary.light',
          },
          '& .MuiListItemText-primary': {
            color: isActive ? 'primary.main' : 'text.primary',
            fontWeight: isActive ? 600 : 500,
          }
        },
        
        '&:active': {
          transform: 'translateX(2px)',
          backgroundColor: 'rgba(25, 118, 210, 0.25)',
        },
          
          '&:focus': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          }
        }}
    >
      <ListItemIcon
        sx={{
          color: isActive ? 'primary.main' : 'grey.800',
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          transition: 'color 0.2s ease-in-out',
        }}
      >
        {icon}
      </ListItemIcon>

      {isDrawerOpen && (
        <ListItemText
          primary={label}
          sx={{
            ml: 1,
            "& .MuiListItemText-primary": {
              fontSize: "0.875rem",
              fontWeight: isActive ? 600 : 400,
              color: isActive ? 'primary.main' : 'inherit',
              transition: 'all 0.2s ease-in-out',
            }
          }}
        />
      )}
    </ListItem>
  );
};

interface ClassroomItemProps {
  classroom: Classroom;
  isActive: boolean;
  isDrawerOpen: boolean;
  onClick: () => void;
}

const ClassroomItem: React.FC<ClassroomItemProps> = ({
  classroom,
  isActive,
  isDrawerOpen,
  onClick,
}) => (
  <ListItem
    onClick={onClick}
    disablePadding
    sx={{
      cursor: "pointer",
      borderRadius: "10px",
      transition: "all 0.2s ease",
      position: "relative",
      display: "flex",
      justifyContent: isDrawerOpen ? "flex-start" : "center",
      alignItems: "center",
      px: isDrawerOpen ? 1 : 0.1,
      py: 0.75,
      backgroundColor: isActive ? "rgba(25, 118, 210, 0.08)" : "transparent",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: "20%",
        height: "60%",
        width: "3px",
        borderRadius: "0 3px 3px 0",
        backgroundColor: isActive ? "primary.main" : "transparent",
        transition: "all 0.2s ease",
      },
      "&:hover": {
        backgroundColor: isActive
          ? "rgba(25, 118, 210, 0.12)"
          : "rgba(0, 0, 0, 0.05)",
        "&::before": {
          backgroundColor: isActive ? "primary.main" : "rgba(25,118,210,0.3)",
          top: "15%",
          height: "70%",
        },
      },
      "&:active": {
        transform: "scale(0.98)",
      },
    }}
  >
 
    <Box
      sx={{
        borderRadius: "8px",
        overflow: "hidden",
        display: "flex",
      }}
    >
      {renderAvatar(classroom.id, classroom.name, classroom.image)}
    </Box>

    {isDrawerOpen && (
      <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: isActive ? 700 : 500,
            fontSize: "0.85rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: isActive ? "primary.main" : "text.primary",
            lineHeight: 1.3,
            transition: "all 0.2s ease",
          }}
        >
          {classroom.name}
        </Typography>
        {classroom.hub_name && (
          <Typography
            variant="caption"
            sx={{
              color: "text.disabled",
              fontSize: "0.7rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
              lineHeight: 1.2,
              mt: 0.2,
            }}
          >
            {classroom.hub_name}
          </Typography>
        )}
      </Box>
    )}
  </ListItem>
);

const chatSupport = () => {
    const encodedPhoneNumber = encodeURIComponent(2347059148543);
    const messageLines = [
        'Hi, Good Day',
        "I'm a student from ELMS"
      ];
      
    const encodedMessage = messageLines.map(line => encodeURIComponent(line)).join('%0A');
    
    const whatsappLink = `https://api.whatsapp.com/send?phone=${encodedPhoneNumber}&text=${encodedMessage}`;
  
    // Open the WhatsApp chat link in a new window or tab
    window.open(whatsappLink, '_blank');
};



const expandedWidth = 280;
const collapsedWidth = 60;
const hiddenWidth = 0;

interface NavBarLayoutProps {
  children: React.ReactNode;
}

function NavBarLayout({ children }: NavBarLayoutProps) {
  const { classroom_id } = useParams<{classroom_id: string}>();
  const location = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [drawerState, setDrawerState] = useState<'expanded' | 'collapsed' | 'hidden'>(
    isDesktop ? 'expanded' : 'collapsed'
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [createClassroomDialog, setCreateClassroomDialog] = useState(false);
  const [filteredClassrooms, setFilteredClassrooms] = useState<Classroom[]>([]);

  const { classrooms, getClassrooms } = useStudentActions();

  const isNavItemActive = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const getCurrentDrawerWidth = () => {
    switch (drawerState) {
      case 'expanded': return expandedWidth;
      case 'collapsed': return collapsedWidth;
      case 'hidden': return hiddenWidth;
      default: return collapsedWidth;
    }
  };

  const isDrawerOpen = drawerState === 'expanded';
  const isDrawerVisible = drawerState !== 'hidden';
  const drawerWidth = getCurrentDrawerWidth();

  const toggleDrawer = () => {
    if (drawerState === 'expanded') {
      setDrawerState('collapsed');
    } else if (drawerState === 'collapsed') {
      setDrawerState('expanded');
    } else {
      setDrawerState('collapsed');
    }
  };

  const hideDrawer = () => {
    setDrawerState('hidden');
  };

  const showDrawer = () => {
    setDrawerState('collapsed');
  };

  const expandDrawer = () => {
    if (drawerState !== 'expanded') {
      setDrawerState('expanded');
    }
  };

  const handleSearchClick = () => {
    expandDrawer();
  };

  useEffect(() => {
    if (classrooms) return;

    const loadClassrooms = async () => {
      await getClassrooms();
    };

    loadClassrooms();
  }, []);

  useEffect(() => {
    setDrawerState(isDesktop ? 'expanded' : 'collapsed');
  }, [isDesktop]);

  useEffect(() => {
    if (!classrooms) return;
    
    const filtered = Object.values(classrooms)
    .filter((cls) =>
      cls.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort(
      (a, b) =>
        new Date(b.enrollment_date).getTime() - new Date(a.enrollment_date).getTime()
    );

    setFilteredClassrooms(filtered);
  }, [classrooms, searchTerm]);


  return (
    <Box
      sx={{
        backgroundColor: "grey.200",
        minHeight: "100vh"
      }}
    >
      {/* <CssBaseline /> */}
      
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          backgroundColor: "grey.200",
          zIndex: theme.zIndex.drawer + 1,
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Toolbar>
          {drawerState === 'hidden' && (
            <IconButton
              onClick={showDrawer}
              sx={{ mr: 2, color: '#grey.900' }}
            >
              <Menu />
            </IconButton>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              justifyContent: "center",
            }}
          >
            <img 
              src="/logo.png" 
              alt="Logo" 
              style={{ height: 60 }}
            />
          </Box>

           <IconButton onClick={chatSupport}>
            <SupportAgent/>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        open
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "grey.200",
            borderRight: "1px solid grey.200",
            overflow: "hidden",
          },
        }}
      >
      <Box sx={{ marginTop: 10, width: '100%' }}>
        <List>
          <NavItem
            icon={<Dashboard />}
            label="Dashboard"
            onClick={() => navigate("/")}
            isDrawerOpen={isDrawerOpen}
            isActive={isNavItemActive('/')}
          />
        </List>

        <Divider sx={{ mx: isDrawerOpen ? 2 : 1, my: 1 }} />

        <Box sx={{ px: isDrawerOpen ? 2 : 0, py: 0 }}>
          {isDrawerOpen ? (
            <TextField
              fullWidth
              size="small"
              placeholder="Search classrooms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "white",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1976d2",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1976d2",
                  },
                },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#666" }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", py: 0 }}>
              <Tooltip title="Search classrooms" placement="right">
                <IconButton
                  size="large"
                  onClick={handleSearchClick}
                  sx={{
                    width: 56,
                    height: 56,
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <Search />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

    <Box
      sx={{
        flexGrow: 1,
        overflowY: "auto",
        overflowX: "hidden",
        px: isDrawerOpen ? 2 : 1,
        mt: 1,
      }}
    >
      <List >
        {filteredClassrooms.length > 0 ? (
          filteredClassrooms.map((cls) => (
            <ClassroomItem
              key={cls.id}
              classroom={cls}
              isActive={Number(classroom_id) === cls.id}
              isDrawerOpen={isDrawerOpen}
              onClick={() => {
                navigate(`/classrooms/${cls.id}`);
              }}
            />
          ))
        ) : (
          isDrawerOpen && searchTerm && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No classrooms found
              </Typography>
            </Box>
          )
        )}
      </List>
    </Box>

      <Box sx={{ width: '100%' }}>
        <Divider sx={{ mx: isDrawerOpen ? 2 : 1, my: 1 }} />

        <List>

          <NavItem
            icon={<AccountCircle />}
            label="Account"
            onClick={() => navigate("/account")}
            isDrawerOpen={isDrawerOpen}
            isActive={isNavItemActive('/account')}
          />
        </List>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          px={isDrawerOpen ? 2 : 1}
        >
          <Button
            onClick={toggleDrawer}
            fullWidth
            startIcon={isDrawerOpen ? <ChevronLeft /> : <ChevronRight />}
            sx={{
              justifyContent: isDrawerOpen ? "flex-start" : "center",
              color: "#666",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              paddingX: 0,
              minHeight: 40,
              borderRadius: 2,
            }}
          >
            {isDrawerOpen && "Collapse"}
          </Button>

          <Button
            onClick={hideDrawer}
            fullWidth
            startIcon={<Close />}
            sx={{
              justifyContent: isDrawerOpen ? "flex-start" : "center",
              color: "#666",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              paddingX: isDrawerOpen ? 1.5 : 0,
              minHeight: 40,
              borderRadius: 2,
            }}
          >
            {isDrawerOpen && "Hide"}
          </Button>
        </Box>
      </Box>
      </Drawer>

   
      <Box>
        <Toolbar />
        <Box
          sx={{
            backgroundColor: "#fff",
            borderTopLeftRadius: drawerWidth > 0 ? 40 : 0,
            padding: 2,
            flexGrow: 1,
            minHeight: '100vh',
            marginLeft: `${drawerWidth}px`,
            transition: theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
       
          {children}
        </Box>
      </Box>

      
    </Box>
  );
}

export default memo(NavBarLayout);